import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import classNames from 'classnames'

// evertel
import { useDidMountEffect } from '@evertel/hooks'
import { Icon, Text, Row, Button, Card, CardBody, CardFooter, Badge, useUI } from '@evertel/web/ui'
import { Delegator } from '@evertel/schema-parser'
import Wrapper from './Wrapper'
import { SchemaFieldItemProps } from '../schema-builder/data/_schemaItemProps'

export interface ListWriteWidgetProps {
    id: string
    htmlId?: string
    value: Record<string, any>[]
    errors?: Record<string, any>[]
    onChange: (id: string, value: Record<string, any>[]) => void
    title: string
    description?: string
    hint?: string
    required?: boolean
    item?: any
    items?: any[]
    scope?: string
    internalSchemaId?: string
    schemaId?: number
    documentId?: number
    className?: string
    onClickToEditListSchema: (internalSchemaId: string) => void
}

// to prevent a new object from being generated each time and causing rerenders
const DEFAULT_EMPTY1=[]
const DEFAULT_EMPTY2=[]

const ListWriteWidget: React.FC<ListWriteWidgetProps> = ({
    id,
    htmlId,
    title,
    item,
    items,
    onChange,
    value: initialSubforms = DEFAULT_EMPTY1,
    errors = DEFAULT_EMPTY2,
    scope,
    internalSchemaId,
    onClickToEditListSchema,
    ...otherProps
}) => {

    //console.log(id, title, initialValue, items)

    const { isConfirmed } = useUI()

    const [subForms, setSubForms] = useState<Record<string, any>[]>(initialSubforms) // handles bad data from EverDocs v1
    const [lastAddedIndex, setLastAddedIndex] = useState<number | null>(null)
    const listItems = (item) ? item.items : items

    const newSubformRefs = useRef<Map<number, HTMLDivElement>>(new Map())

    const labelId = `label-${htmlId}`
    const descriptionId = `description-${htmlId}`
    const errorId = `error-${htmlId}`


    const setSubformRef = useCallback((element: HTMLDivElement | null, index: number) => {
        if (element) {
            newSubformRefs.current.set(index, element)
        } else {
            newSubformRefs.current.delete(index)
        }
    }, [])

    const findFirstFocusableElement = (container: Element): HTMLElement | null => {
        const allElements = Array.from(container.querySelectorAll('*')) as HTMLElement[]

        return allElements.find(element =>
            element.tabIndex >= 0 &&
            element.offsetParent !== null
        ) || null
    }


    useLayoutEffect(() => {
        if (lastAddedIndex !== null) {
            setTimeout(() => {
                const subformElement = newSubformRefs.current.get(lastAddedIndex)

                if (subformElement) {
                    const firstFocusable = findFirstFocusableElement(subformElement)
                    firstFocusable?.focus()
                }

                setLastAddedIndex(null)
            }, 10)
        }
    }, [lastAddedIndex])

    useDidMountEffect(() => {
        onChange && onChange(id, subForms)
    }, [subForms])

    const onAdd = () => {
        setSubForms((existing) => {
            const newSubForms = existing.concat({
                _id: Date.now()
            })
            // Set the index of the newly added subform
            setLastAddedIndex(newSubForms.length - 1)
            return newSubForms
        })
    }

    const onDelete = async (index: number) => {
        const deleteIt = async () => {
            setSubForms((prev) => {
                return prev.filter((_, idx) => idx !== index)
            })
        }

        const confirmed = await isConfirmed({
            title: `Delete this ${title} item?`,
            message: `Are you sure you want to delete this ${title} item from this document?`,
            acceptButton: {
                label: 'Delete',
                color: 'danger'
            }
        })
        if (confirmed) {
            deleteIt()
        }
    }

    return (
        <Wrapper
            title={title}
            labelId={labelId}
            descriptionId={descriptionId}
            errorId={errorId}
            {...otherProps}
            labelClassName="h4"
            className={classNames(otherProps.className, 'ListWriteWidget')}
        >
            <Card className={classNames('bg-secondary-subtle', {'list': scope === 'schemaBuilder'})}>
                <CardBody>
                    {/* in the schemaBuilder we want to show the form even though there's no data (value) */}
                    {(scope === 'schemaBuilder') ? (
                        <div inert>
                            {listItems?.map(({id, type, ...itemProps}: {id: string, type: string}, idx: number) =>
                                <div key={idx}>
                                    <Delegator
                                        id={id}
                                        type={type}
                                        index={idx.toString()}
                                        scope={scope}
                                        {...otherProps}
                                        {...itemProps}
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            {subForms?.length === 0 && (
                                <div className="mb-3">No {title} items added yet. Use the button below to add one.</div>
                            )}
                            {subForms?.map(({ _id, ...itemValue }, subformIndex) => {
                                // Generate unique section identifier for this subform
                                const sectionHeadingId = `section-heading-${id}-${subformIndex}`

                                // Find the error object for the current subForm item
                                // `errors` is the array of error objects for the list, passed as a prop
                                const currentItemErrors = errors?.find(err => err && err._id === _id)

                                return (
                                    <Card
                                        key={`v-${subformIndex}`}
                                        role="group"
                                        aria-labelledby={sectionHeadingId}
                                        className="mb-3"
                                        data-subform-index={subformIndex}
                                        ref={(el) => setSubformRef(el as HTMLDivElement, subformIndex)}
                                    >
                                        <CardBody>
                                            <h4 id={sectionHeadingId} className="visually-hidden" >
                                                {title} #{subformIndex + 1}
                                            </h4>
                                            {listItems?.map((listItem: SchemaFieldItemProps, itemIndex: number) => {
                                                // Create unique IDs for each field in this subform
                                                return (
                                                    <Delegator
                                                        key={`listWidget-${itemIndex}`}
                                                        value={itemValue}
                                                        errors={currentItemErrors}
                                                        index={`${subformIndex}-${itemIndex}`}
                                                        onChange={(itemId: string, itemValue: string) => {
                                                            setSubForms((prev) => {
                                                                return prev.map((v) => {
                                                                    if (v._id !== _id) {
                                                                        return v
                                                                    }
                                                                    return {
                                                                        _id: _id,
                                                                        ...v,
                                                                        [itemId]: itemValue
                                                                    }
                                                                })
                                                            })
                                                        }}
                                                        {...otherProps}
                                                        {...listItem}
                                                    />
                                                )
                                            })}
                                            <Row align="right">
                                                <Button
                                                    color="danger"
                                                    ghost
                                                    onClick={() => onDelete(subformIndex)}
                                                    aria-label={`Delete ${title} #${subformIndex + 1}`}
                                                >
                                                    <Text size="small"> Delete </Text>
                                                </Button>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                )
                            })}
                        </>
                    )}
                </CardBody>
                <CardFooter>
                    <Button color="primary" onClick={onAdd} size="sm">
                        <Icon name="plus" className="me-2" />
                        Add New {title}
                    </Button>
                </CardFooter>
            </Card>
        </Wrapper>
    )
}

// ----------------------------------------------------------------------------------------

export interface ListReadWidgetProps {
    id?: string
    htmlId?: string
    value: Record<string, any>[]
    title?: string
    empty?: string
    schemaId?: number
    documentId?: number
    item?: Record<string, any>
    items?: SchemaFieldItemProps[]
    className?: string
}

const ListReadWidget: React.FC<ListReadWidgetProps> = ({
    htmlId,
    value: subForms = [],
    title,
    item,
    items,
    ...otherProps
}) => {

    //console.log('LIST', title, {item}, {items}, {value}, {...otherProps})

    const listItems = (item) ? item.items : items // backwards campatibility

    const labelId = `label-${htmlId}`
    const descriptionId = `description-${htmlId}`

    // @ts-expect-error: no-unused-vars
    const { internalSchemaId, ...remainingProps } = otherProps

    return (
        <Wrapper
            title={title}
            labelId={labelId}
            descriptionId={descriptionId}
            {...remainingProps}
            labelClassName="h4"
            className={classNames(remainingProps.className, 'ListReadWidget')}
        >
            {subForms?.length === 0 && (
                <div className="mb-3">No {title} items added.</div>
            )}
            {subForms?.map(({_id, ...itemValue}, subformIndex) => {
                // Generate unique section identifier for this subform
                const sectionHeadingId = `section-heading-${htmlId}-${subformIndex}`

                return (
                    <Card
                        key={`v-${subformIndex}`}
                        role="group"
                        aria-labelledby={sectionHeadingId}
                        className="mb-3"
                    >
                        <CardBody>
                            <h3 id={sectionHeadingId} className="visually-hidden" >
                                {title} #{subformIndex + 1}
                            </h3>
                            {listItems?.map((listItem: SchemaFieldItemProps, itemIndex: number) =>
                                <Delegator
                                    key={`listWidget-${itemIndex}`}
                                    value={itemValue}
                                    index={`${subformIndex}-${itemIndex}`}
                                    {...otherProps}
                                    {...listItem}
                                />
                            )}
                        </CardBody>
                    </Card>
                )
            })}
        </Wrapper>
    )
}

export {
    ListWriteWidget,
    ListReadWidget
}
